import {
  ACKNOWLEDGE_STEP_ASSESSMENT_PLAN,
  GENERATE_STEP_ASSESSMENT_PLAN,
  SUGGEST_STEP_ASSESSMENT_PLAN_COVERAGE,
} from "@lib/graphql/mutations";
import type { StepAssessmentPlanData, StepPlanCoverageEntry } from "@lib/selectionSteps";
import { Minus, Plus, Sparkles, Target } from "lucide-react";
import { useRef, useState } from "react";
import { useMutation } from "urql";

export interface DimensionOption {
  continuityId: string;
  name: string;
}

// Re-exports from the canonical location so existing consumers can keep
// importing from this file.
export type CoverageEntry = StepPlanCoverageEntry;
export type StepAssessmentPlan = StepAssessmentPlanData;

interface StepAssessmentPlanFieldsProps {
  selectionStepId: string;
  // Optional — when omitted, the section header is "Assessment plan" without
  // a trailing step name (used inside StepCard, whose own header already
  // shows the step label).
  stepLabel?: string;
  value: StepAssessmentPlan;
  // Editable fields are fully controlled. Onwer state lives in the parent so
  // the cohesive editor can diff and batch-save plan + structural edits.
  onChange: (next: { intent: string | null; coverage: CoverageEntry[] }) => void;
  dimensions: DimensionOption[];
  // True when the assessment matrix's bands no longer line up with the
  // current blueprint dimensions. When set, all AI Suggest CTAs are disabled
  // and a single hint banner directs the user to regenerate the matrix —
  // otherwise they'd hit the SuggestStepCoverage "out of sync" failure path.
  matrixOutOfSync?: boolean;
  // Fired after AI-suggestion or acknowledge — the parent should refetch.
  onSaved: () => void;
}

const DEPTHS = [
  { key: "sample", label: "Screen" },
  { key: "probe", label: "Probe" },
  { key: "deep_dive", label: "Deep dive" },
] as const;

function planIsEffectivelyEmpty(plan: StepAssessmentPlan): boolean {
  return (plan.intent ?? "").trim() === "" && plan.coverage.length === 0;
}

export function StepAssessmentPlanFields({
  selectionStepId,
  stepLabel,
  value,
  onChange,
  dimensions,
  matrixOutOfSync = false,
  onSaved,
}: StepAssessmentPlanFieldsProps) {
  // When the manual-edits gateway blocks, remember which workflow the user
  // is trying to run so the confirm-overwrite button re-fires the right one.
  const [pendingConfirm, setPendingConfirm] = useState<null | "plan" | "coverage">(null);
  const intentRef = useRef<HTMLTextAreaElement | null>(null);
  const [{ fetching: suggesting }, suggest] = useMutation(GENERATE_STEP_ASSESSMENT_PLAN);
  const [{ fetching: suggestingCoverage }, suggestCoverage] = useMutation(
    SUGGEST_STEP_ASSESSMENT_PLAN_COVERAGE,
  );
  const [, acknowledge] = useMutation(ACKNOWLEDGE_STEP_ASSESSMENT_PLAN);

  const intent = value.intent ?? "";
  const coverage = value.coverage;

  const updateIntent = (next: string) => {
    onChange({ intent: next === "" ? null : next, coverage });
  };

  const addCoverageRow = () => {
    const used = new Set(coverage.map((c) => c.dimensionId));
    const firstUnused = dimensions.find((d) => !used.has(d.continuityId));
    if (!firstUnused) return;
    onChange({
      intent: value.intent,
      coverage: [...coverage, { dimensionId: firstUnused.continuityId, depth: "probe" }],
    });
  };

  const updateCoverageRow = (index: number, patch: Partial<CoverageEntry>) => {
    onChange({
      intent: value.intent,
      coverage: coverage.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });
  };

  const removeCoverageRow = (index: number) => {
    onChange({
      intent: value.intent,
      coverage: coverage.filter((_, i) => i !== index),
    });
  };

  const fireSuggest = async (confirmOverwrite: boolean) => {
    const result = await suggest({
      input: { selectionStepId, confirmOverwrite },
    });
    const data = result.data?.generateStepAssessmentPlan;
    if (data?.status === "manual_edits_present") {
      setPendingConfirm("plan");
      return;
    }
    setPendingConfirm(null);
    onSaved();
  };

  const fireSuggestCoverage = async (confirmOverwrite: boolean) => {
    const result = await suggestCoverage({
      input: { selectionStepId, confirmOverwrite },
    });
    const data = result.data?.suggestStepAssessmentPlanCoverage;
    if (data?.status === "manual_edits_present") {
      setPendingConfirm("coverage");
      return;
    }
    setPendingConfirm(null);
    onSaved();
  };

  const fireAcknowledge = async () => {
    await acknowledge({ input: { selectionStepId } });
    onSaved();
  };

  const isEmpty = planIsEffectivelyEmpty(value);
  // Every coverage row points at a dimension no longer in the current matrix
  // — the plan is technically populated but functionally unusable. Surfaces a
  // "Suggest plan" affordance even though `isEmpty` is false. #2316.
  const allCoverageStale =
    value.coverage.length > 0 && value.coverage.every((c) => c.dimensionInCurrentMatrix === false);
  // Intent paragraph is written but coverage is empty — surface a focused
  // "Suggest coverage" CTA that preserves the intent and fills in the grid.
  const intentOnly = (value.intent ?? "").trim() !== "" && value.coverage.length === 0;
  const isSystemDraft =
    value.versionOrigin === "system" && value.acknowledgedAt === null && !isEmpty;
  const isSystemAccepted = value.versionOrigin === "system" && value.acknowledgedAt !== null;
  const matrixReady = dimensions.length > 0;
  // Suggestions allocate against the current matrix bands. When the matrix
  // is out of sync with the current blueprint (post #2316 regenerate without
  // cascade), the per-step Suggest paths would fail with the new "out of
  // sync" diagnostic — disable them up-front and tell the user where to fix
  // it.
  const suggestionsBlocked = matrixOutOfSync;

  const intentId = `sap-intent-${selectionStepId}`;

  return (
    <div className="space-y-3" data-testid={`step-assessment-plan-${selectionStepId}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Target size={14} className="text-text-secondary" />
          <h4 className="text-sm font-medium text-text-secondary">
            {stepLabel ? <>Assessment plan &mdash; {stepLabel}</> : <>Assessment plan</>}
          </h4>
        </div>
        {value.versionNumber != null &&
          (isSystemDraft ? (
            <span
              data-testid={`sap-ai-badge-${selectionStepId}`}
              className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
            >
              <Sparkles size={10} /> AI suggested &middot; review
            </span>
          ) : isSystemAccepted ? (
            <span className="inline-flex items-center gap-1 rounded bg-card-hover px-2 py-0.5 text-xs text-text-faint">
              <Sparkles size={10} /> v{value.versionNumber} &middot; system (accepted)
            </span>
          ) : (
            <span className="rounded bg-card-hover px-2 py-0.5 text-xs text-text-faint">
              v{value.versionNumber} &middot; {value.versionOrigin ?? "user"}
            </span>
          ))}
      </div>

      {suggestionsBlocked && (
        <div
          data-testid={`sap-matrix-out-of-sync-${selectionStepId}`}
          className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800"
        >
          <span className="flex-1">
            Assessment matrix is out of sync with the current blueprint — its bands reference
            dimensions that are no longer effective. AI Suggest is disabled here until the matrix is
            regenerated from the Assessment tab.
          </span>
        </div>
      )}

      {isEmpty && (
        <div className="rounded border border-dashed border-input-border bg-card p-3">
          {matrixReady ? (
            <>
              <p className="mb-2 text-xs text-text-secondary">
                No plan yet. Let AI propose intent and coverage from the blueprint — you can review
                and edit before it becomes yours.
              </p>
              <button
                type="button"
                onClick={() => void fireSuggest(false)}
                disabled={suggesting || suggestionsBlocked}
                data-testid={`sap-suggest-${selectionStepId}`}
                className="inline-flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles size={12} /> {suggesting ? "Suggesting…" : "Suggest plan"}
              </button>
            </>
          ) : (
            <p className="text-xs text-text-faint">
              AI-suggested plans need a Role Blueprint and Assessment Matrix.
            </p>
          )}
        </div>
      )}

      {!isEmpty && allCoverageStale && matrixReady && (
        <div className="flex items-center gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
          <span className="flex-1">
            Every coverage row references a dimension no longer in the matrix. Replace coverage
            against the current dimensions — or suggest a fresh plan to also rewrite the intent.
          </span>
          <button
            type="button"
            onClick={() => void fireSuggestCoverage(false)}
            disabled={suggestingCoverage || suggestionsBlocked}
            data-testid={`sap-resuggest-coverage-${selectionStepId}`}
            className="inline-flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles size={12} /> {suggestingCoverage ? "Suggesting…" : "Suggest coverage only"}
          </button>
          <button
            type="button"
            onClick={() => void fireSuggest(false)}
            disabled={suggesting || suggestionsBlocked}
            data-testid={`sap-resuggest-${selectionStepId}`}
            className="inline-flex items-center gap-1 rounded bg-card-hover px-2 py-1 text-xs font-medium text-text-primary hover:bg-card-hover/80 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles size={12} /> {suggesting ? "Suggesting…" : "Suggest plan"}
          </button>
        </div>
      )}

      {intentOnly && matrixReady && (
        <div className="rounded border border-dashed border-input-border bg-card p-3">
          <p className="mb-2 text-xs text-text-secondary">
            Intent is set but no coverage yet. Let AI propose coverage against the current matrix
            dimensions.
          </p>
          <button
            type="button"
            onClick={() => void fireSuggestCoverage(false)}
            disabled={suggestingCoverage || suggestionsBlocked}
            data-testid={`sap-suggest-coverage-empty-${selectionStepId}`}
            className="inline-flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Sparkles size={12} /> {suggestingCoverage ? "Suggesting…" : "Suggest coverage"}
          </button>
        </div>
      )}

      {isSystemDraft && (
        <div className="flex items-center gap-2 rounded bg-amber-50 p-2 text-xs text-amber-800">
          <span className="flex-1">
            This draft was suggested by AI. Accept to keep as-is, or edit any field to make it your
            own.
          </span>
          <button
            type="button"
            onClick={() => void fireAcknowledge()}
            data-testid={`sap-accept-${selectionStepId}`}
            className="rounded bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-700"
          >
            Accept
          </button>
          <button
            type="button"
            onClick={() => intentRef.current?.focus()}
            data-testid={`sap-edit-${selectionStepId}`}
            className="rounded bg-card-hover px-2 py-1 text-xs font-medium text-text-primary hover:bg-card-hover/80"
          >
            Edit
          </button>
        </div>
      )}

      {pendingConfirm !== null && (
        <div className="flex items-center gap-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
          <span className="flex-1">
            {pendingConfirm === "coverage"
              ? "This step's plan has manual edits. Replacing coverage will supersede the current version (intent carries forward; history is preserved)."
              : "This step's plan has manual edits. Re-suggesting will replace them (history is preserved)."}
          </span>
          <button
            type="button"
            onClick={() =>
              pendingConfirm === "coverage"
                ? void fireSuggestCoverage(true)
                : void fireSuggest(true)
            }
            disabled={pendingConfirm === "coverage" ? suggestingCoverage : suggesting}
            data-testid={`sap-confirm-overwrite-${selectionStepId}`}
            className="rounded bg-amber-600 px-2 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          >
            Overwrite with AI suggestion
          </button>
          <button
            type="button"
            onClick={() => setPendingConfirm(null)}
            className="rounded bg-card-hover px-2 py-1 text-xs font-medium text-text-primary"
          >
            Cancel
          </button>
        </div>
      )}

      <div>
        <label htmlFor={intentId} className="mb-1 block text-xs font-medium text-text-secondary">
          What is this step trying to learn?
        </label>
        <textarea
          id={intentId}
          ref={intentRef}
          value={intent}
          onChange={(e) => updateIntent(e.target.value)}
          aria-label="Assessment intent"
          placeholder="The intent for this step — what evidence are you trying to gather?"
          rows={3}
          className="w-full rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>

      <div>
        <span className="mb-1 block text-xs font-medium text-text-secondary">Coverage</span>
        {dimensions.length === 0 ? (
          <p className="text-xs text-text-faint">
            Generate the Assessment Matrix to pick coverage dimensions for this step.
          </p>
        ) : (
          <div className="space-y-1">
            {coverage.map((row, i) => {
              const rowId = `sap-${selectionStepId}-cov-${i}`;
              const usedByOthers = new Set(
                coverage.flatMap((c, idx) => (idx === i ? [] : [c.dimensionId])),
              );
              const selectableDimensions = dimensions.filter(
                (d) => d.continuityId === row.dimensionId || !usedByOthers.has(d.continuityId),
              );
              const inCurrent =
                row.dimensionInCurrentMatrix ??
                dimensions.some((d) => d.continuityId === row.dimensionId);
              const historicalName = row.dimensionName?.trim();
              const orphanLabel = historicalName
                ? `${historicalName} (no longer in matrix)`
                : "(dimension no longer in matrix)";
              return (
                <div
                  key={rowId}
                  className="flex items-center gap-2"
                  data-testid={`sap-coverage-row-${i}`}
                >
                  <select
                    value={row.dimensionId}
                    onChange={(e) => updateCoverageRow(i, { dimensionId: e.target.value })}
                    aria-label="Coverage dimension"
                    className="flex-1 rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  >
                    {selectableDimensions.map((d) => (
                      <option key={d.continuityId} value={d.continuityId}>
                        {d.name}
                      </option>
                    ))}
                    {!inCurrent && <option value={row.dimensionId}>{orphanLabel}</option>}
                  </select>
                  {!inCurrent && (
                    <span
                      data-testid={`sap-coverage-orphan-${i}`}
                      className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800"
                      title="This dimension is no longer in the current Assessment Matrix. It will be dropped when you save — pick a current dimension to replace it, or remove the row."
                    >
                      no longer in matrix
                    </span>
                  )}
                  <select
                    value={row.depth}
                    onChange={(e) => updateCoverageRow(i, { depth: e.target.value })}
                    aria-label="Coverage depth"
                    className="rounded border border-input-border bg-card px-2 py-1 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                  >
                    {DEPTHS.map((d) => (
                      <option key={d.key} value={d.key}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => removeCoverageRow(i)}
                    aria-label="Remove coverage row"
                    className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
                  >
                    <Minus size={14} />
                  </button>
                </div>
              );
            })}
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={addCoverageRow}
                disabled={coverage.length >= dimensions.length}
                className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Plus size={12} /> Add coverage
              </button>
              {coverage.length > 0 && !allCoverageStale && (
                <button
                  type="button"
                  onClick={() => void fireSuggestCoverage(false)}
                  disabled={suggestingCoverage || suggestionsBlocked}
                  data-testid={`sap-suggest-coverage-${selectionStepId}`}
                  className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Sparkles size={12} /> {suggestingCoverage ? "Suggesting…" : "Suggest coverage"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
